/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import Container from '../../../templates/Container/Container'
import EditionWrapper from '../../../templates/EditionWrapper'
import SlideEditConfig from './SlideEditConfig'
import { SlideModelProps } from './types'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
// slide placeholder
const Slide: React.FC<EditableComponentPropsType<SlideModelProps>> =
  withComponentRefresh((props) => {
    const { id, gtm, ...rest } = props.model
    const editableProps = useExtractEditableProps(props, rest)

    return (
      <EditableComponent {...editableProps}>
        {AuthoringUtils.isInEditor() && (
          <EditionWrapper label={SlideEditConfig.emptyLabel}>
            <Container {...props} />
          </EditionWrapper>
        )}
      </EditableComponent>
    )
  })

const SlideWithErrorBoundary = withErrorBoundary(Slide, {
  componentName: 'Slide',
  fallback: <ErrorFallback componentName="Slide" />
})

export default SlideWithErrorBoundary
